import { Component, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription, filter } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { TERMS } from 'src/app/conf/terms';
import { InvitationData } from 'src/app/model/api/invitation-data';
import { Headquarter } from 'src/app/model/headquarter';
import { Term, UserTerm } from 'src/app/model/term';
import { User, maxRegistrationDate } from 'src/app/model/user';
import { AppState } from 'src/app/store/app.reducer';
import * as UserActions from 'src/app/store/user/user.actions';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { COUNTRIES, DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';
import * as moment from 'moment';
import { PASSWORD_REGEXP, PHONE_REGEXP } from 'src/app/conf/user';
import * as NotificationsActions from "../../../../store/notifications/notifications.actions";
import { selectCurrentLang, selectRoleExplainedTechnician, selectUser } from 'src/app/store/user/user.selector';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from '../../stdlayoutlib/classes.stdlayoutlib';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Component({ // user signup component
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styles: `
		.noSelectLabel-profile {
			user-select: none;
			-webkit-user-select: none;
		}
	`
})

export class ProfileComponent implements OnInit, OnDestroy {
	// Store selectors
	headquartersSel$: Observable<Headquarter[] | undefined>;
	headquartersSub: Subscription | undefined;

	// Design
	screenWidth: number;

	// PARAM
	invitationData = new InvitationData();
	fromInvite = false;
	isTechnician = false;
	role = 'user';
	headquarters: Headquarter[];

	minDate: Date = new Date("1900-01-01");
	maxDate: Date = new Date();
	termsAndConditions: Term[] = TERMS;
	languages: Lang[] = LANGUAGES;
	citizenships = COUNTRIES;

	filteredHeadquarters: Headquarter[];
	filteredCountries: any[];

	// Form
	userProfileForm: FormGroup;
	termsArray: FormControl[] = [];
	emailConfirm = '';
	password = '';
	passwordConfirm = '';

	currentUser: User;

	iAmTechnician: boolean;

	typeUserSel$: Observable<boolean | undefined>;
	typeUserSub: Subscription | undefined;

	firstCompile: boolean;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	constructor(
		public renderer: Renderer2,
		public app: AppComponent,
		private route: ActivatedRoute,
		private store: Store<AppState>,
		private router: Router,
	) {
		this.headquartersSel$ = this.store.select('user', 'headquarters');
	}

	ngOnInit(): void {
		this.loadHeadquarters();
		this.role = this.route.snapshot.params['role'];
		this.isTechnician = this.role === 'technician';

		this.store.select(selectUser).pipe(
			filter((user) => !!user)
		).subscribe(user => {
			this.currentUser = user;
			if (this.currentUser !== undefined) {
				this.buildForm();
			}
		});

		this.initSelector();
		this.subscribeAll();

		if (this.iAmTechnician) {
			this.store.dispatch(NotificationsActions.setTopbarTitleId({
				stringId: 'TECHNICIAN_PROFILE'
			}));
		} else {
			this.store.dispatch(NotificationsActions.setTopbarTitleId({
				stringId: 'USER_PROFILE'
			}));
		}

		this.onResize();

		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventCustomPageView({ params: { page_title: 'Profile' } }));
		}, 2000);

		StandardNewValidator.setFirstCompile(true);
	}

	initSelector(): void {
		this.store.select(selectUser).pipe(
			filter((user) => !!user)
		).subscribe(user => {
			this.currentUser = user;
			if (this.currentUser !== undefined) {
				this.buildForm();
			}
		});
		this.typeUserSel$ = this.store.select(selectRoleExplainedTechnician);
		this.currentLang$ = this.store.select(selectCurrentLang);
	}

	subscribeAll(): void {
		this.typeUserSub = this.typeUserSel$?.subscribe(
			(isTechnician: boolean) => {
				this.iAmTechnician = isTechnician;
				this.loadHeadquarters();
			}
		);
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
	}

	ngOnDestroy(): void {
		this.typeUserSub?.unsubscribe();
	}

	booleanControl(required: boolean, trueValue: any, falseValue: any): FormControl {
		return new FormControl(falseValue, required ? Validators.requiredTrue : null);
	}

	buildForm(): void {
		this.maxDate = maxRegistrationDate();
		const newsletterSubscriptionTer = this.currentUser?.UserTerms.find(l => l.Id === 3);
		const newsletterSubscriptionTerFlag = newsletterSubscriptionTer !== undefined && newsletterSubscriptionTer.Value === 'accepted';
		// const transferDataGroupCompaniesTer = this.currentUser.UserTerms.find(l => l.Id === 9);
		// const transferDataGroupCompaniesTerFlag = transferDataGroupCompaniesTer !== undefined && transferDataGroupCompaniesTer.Value === 'accepted';
		let countryNameSel;
		if (this.currentUser !== undefined && this.currentUser.CountryName !== undefined) {
			countryNameSel = this.citizenships.find(l => l.name === this.currentUser.CountryName);
		} else {
			countryNameSel = null;
		}
		let headquarterSel;
		if (this.currentUser !== undefined && this.currentUser.HeadQuarterId !== undefined && this.headquarters !== undefined) {
			headquarterSel = this.headquarters.find(l => l.Id === this.currentUser.HeadQuarterId);
		}
		this.userProfileForm = new FormGroup({
			'firstName': new FormControl(this.currentUser.FirstName, [Validators.required, Validators.maxLength(254)]),
			'lastName': new FormControl(this.currentUser.LastName, [Validators.required, Validators.maxLength(254)]),
			'birthDate': new FormControl(moment(this.currentUser.BirthDate).toDate()),
			'language': new FormControl(this.currentUser.Language, Validators.required),
			'email': new FormControl({ value: this.currentUser.Email, disabled: this.invitationData.Email !== undefined }),
			// [Validators.required, Validators.email]),
			'phone': new FormControl(this.currentUser.Phone, [Validators.pattern(PHONE_REGEXP), Validators.maxLength(20)]),
			'countryName': new FormControl(countryNameSel, [Validators.required]),
			'companyName': new FormControl(this.currentUser.CompanyName),
			'headquarter': new FormControl(headquarterSel),
			'password': new FormControl(this.password, [Validators.pattern(PASSWORD_REGEXP)]),
			'passwordConfirm': new FormControl(this.passwordConfirm, [Validators.pattern(PASSWORD_REGEXP)]),
			'NewsletterSubscription': new FormControl(String(newsletterSubscriptionTerFlag)),/*ID = 3 */
			// 'TransferDataGroupCompanies': new FormControl(String(transferDataGroupCompaniesTerFlag)),/*ID = 9 */
		});
		this.userProfileForm.setValidators(CustomValidators.checkMatchingFields('password', 'passwordConfirm'));
		this.f.headquarter.disable();
	}

	closeAccountPage() {
		this.router.navigate(['../close-account'], { relativeTo: this.route });
	}

	filterCountry(event) {
		const filtered: any[] = [];
		const query = event.query;
		if (this.citizenships) {
			for (let i = 0; i < this.citizenships.length; i++) {
				const country = this.citizenships[i];
				if (country.name.toLowerCase().indexOf(query.toLowerCase()) == 0) {
					filtered.push(country);
				}
			}
		}
		this.filteredCountries = filtered;
	}

	filterHeadquarters(event) {
		const filtered: any[] = [];
		const query = event.query;
		for (let i = 0; i < this.headquarters.length; i++) {
			const headquarters = this.headquarters[i];
			if (headquarters.Description.toLowerCase().indexOf(query.toLowerCase()) == 0) {
				filtered.push(headquarters);
			}
		}

		this.filteredHeadquarters = filtered;
	}

	loadHeadquarters(): void {
		if (this.iAmTechnician) {
			this.store.dispatch(UserActions.getHeadQuarters());
		}
		this.headquartersSub = this.headquartersSel$?.subscribe(
			(headquarters) => {
				this.headquarters = headquarters;
				if (this.currentUser !== undefined) {
					this.buildForm();
				}
			}
		);
	}

	selectedTerms(): UserTerm[] {
		const userTerms: UserTerm[] = [];
		/* NewsletterSubscription ID = 3 */
		const NewsletterSubscription: UserTerm = {
			Id: this.termsAndConditions[3].Id,
			Description: this.termsAndConditions[3].Description,
			Value: this.f.NewsletterSubscription.value === 'true' ? 'accepted' : 'not_accepted'
		};
		userTerms.push(NewsletterSubscription);

		/* TransferDataGroupCompanies ID = 9 */
		/* const TransferDataGroupCompanies: UserTerm = {
			Id: this.termsAndConditions[9].Id,
			Description: this.termsAndConditions[9].Description,
			Value:  this.f.TransferDataGroupCompanies.value === 'true'? 'accepted' : 'not_accepted'
		};
		userTerms.push(TransferDataGroupCompanies); */

		return userTerms;
	}

	getTechnicianCode(): string {
		return this.currentUser.TechnicianCode;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.userProfileForm.controls).forEach(key => {
			if (key !== 'phone') {
				const controlErrors: ValidationErrors = this.userProfileForm.get(key).errors;
				if (controlErrors != null) {
					atLeastOneError = true;
				}
			} else {
				if (this.userProfileForm.get(key).value !== "" && this.userProfileForm.get(key).dirty && this.userProfileForm.get(key).errors) {
					atLeastOneError = true;
				}
			}
		});
		return atLeastOneError;
	}

	submit() {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save() {
		const user: User = {
			...this.currentUser,
			FirstName: this.f.firstName.value,
			LastName: this.f.lastName.value,
			BirthDate: moment(this.f.birthDate.value).toDate(),
			Language: this.f.language.value,
			Email: this.f.email.value,
			Phone: this.f.phone.value,
			CountryName: this.f.countryName.value.name,
			UserTerms: this.selectedTerms()
		};
		if (this.iAmTechnician) {
			user.CompanyName = this.f.companyName.value ? this.f.companyName.value : null;
			user.HeadQuarterId = this.f.headquarter.value ? this.f.headquarter.value.Id : null;
		}
		user.Password = '';
		if (this.f.password.valid) {
			user.Password = this.f.password.value;
		}
		this.store.dispatch(UserActions.updateCurrentUser({ user }));
	}

	termControl(t: Term): FormControl {
		if (t.Show === 'checkbox') {
			return this.booleanControl(t.Required, true, false);
		} else {
			// radio group
			return this.booleanControl(t.Required, 'accepted', 'not_accepted');
		}
	}

	get f(): any {
		return this.userProfileForm.controls;
	}

	backToHome(): void {
		if (this.iAmTechnician) {
			this.router.navigate(['../support'], { relativeTo: this.route });
		} else {
			this.router.navigate(['../sites'], { relativeTo: this.route });
		}
	}

	// Design & Error view manage
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	overTablet(): boolean {
		return StandardPageLayout.overTablet(this.screenWidth);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	paddingTopResponsive(): string {
		return StandardPageLayout.paddingTopResponsive(this.screenWidth);
	}

	col4Responsive(): string {
		return StandardPageLayout.col4ResponsiveStd(this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-18rem');
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	justifyMessagesNewValidator(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	showErrorRequiredMessage(type: ProfileComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorConfirmInvalidMessage(type: ProfileComponent["f"]): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type, 'notEquivalentPassword', this.userProfileForm);
	}

	newShowErrors(type: ProfileComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorsConfirmFields(type: ProfileComponent["f"], classType: string): string {
		return StandardNewValidator.showErrorsConfirmFields(type, classType, 'notEquivalentPassword', this.screenWidth, this.userProfileForm);
	}

	needsToBeDirty(type: ProfileComponent["f"], classType: string): string {
		return StandardNewValidator.needsToBeDirty(type, classType, this.screenWidth);
	}

	showErrorInvalidMessage(type: ProfileComponent["f"]): boolean {
		// special custom for 'phone' field on registration & profile pages -> not required and could be === ""
		return type.value != "" ? StandardNewValidator.showErrorInvalidMessage(type) : false;
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}
}
